// ─────────────────────────────────────────────────────────────────
// Sehat AI — Text-to-Speech Utility
//
// Priority: ElevenLabs (Anamyaa voice) → Web Speech API fallback
//
// Race-condition fixes:
//   • currentAbort stays alive through the entire ElevenLabs pipeline
//     (fetch headers + arrayBuffer download + before audio element is created)
//     so stopSpeaking() can cancel at ANY point in the chain.
//   • speechGeneration token: a monotonically-increasing counter ensures
//     that if stopSpeaking() is called, any pending Web Speech voiceschanged
//     callback is silently discarded.
// ─────────────────────────────────────────────────────────────────

import config from '../config';

let currentAudio  = null;   // currently playing <audio> element
let currentAbort  = null;   // AbortController for the in-flight ElevenLabs pipeline

// Monotonic generation counter — incremented every time a new speech is started
// or speech is cancelled. Any async callback that has an old generation token
// is a ghost and must not speak.
let speechGen = 0;

// ── Text cleaning ─────────────────────────────────────────────────
function cleanText(text) {
  return text
    .replace(/[\u{1F300}-\u{1FFFF}]/gu, '')
    .replace(/[★☆•►]/g, '')
    .replace(/₹(\d+)/g, '$1 rupees')
    .replace(/\*+/g, '')
    .replace(/#+\s*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 500);
}

// ── ElevenLabs TTS ────────────────────────────────────────────────
async function speakElevenLabs(text, voiceId, myGen, onStart, onEnd) {
  // One AbortController covers the entire pipeline:
  //   fetch headers → arrayBuffer download → audio playback setup
  const controller = new AbortController();
  currentAbort = controller;

  try {
    // ── 1. HTTP request ─────────────────────────────────────────
    const res = await fetch(`/api/elevenlabs/v1/text-to-speech/${voiceId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        text,
        model_id: 'eleven_flash_v2_5',
        voice_settings: {
          stability: 0.45,
          similarity_boost: 0.80,
          style: 0.30,
          use_speaker_boost: true,
        },
      }),
    });

    if (!res.ok) {
      currentAbort = null;
      const errText = await res.text().catch(() => '');
      throw new Error(`ElevenLabs ${res.status}: ${errText}`);
    }

    // ── 2. Audio download ────────────────────────────────────────
    // Keep currentAbort alive here — stopSpeaking() during download
    // will abort the signal and arrayBuffer() will reject with AbortError.
    const audioBuffer = await res.arrayBuffer();

    // ── 3. Generation check after download ──────────────────────
    // If stopSpeaking() was called while the arrayBuffer was downloading
    // the signal is aborted and we must not play.
    if (controller.signal.aborted || speechGen !== myGen) {
      currentAbort = null;
      return;
    }

    currentAbort = null; // audio element takes over from here

    // ── 4. Play audio ────────────────────────────────────────────
    const blob = new Blob([audioBuffer], { type: 'audio/mpeg' });
    const url  = URL.createObjectURL(blob);
    currentAudio = new Audio(url);

    await new Promise((resolve, reject) => {
      currentAudio.onplay  = () => {
        if (speechGen !== myGen) {
          // Cancelled just as playback started
          currentAudio?.pause();
          URL.revokeObjectURL(url);
          currentAudio = null;
          onEnd?.();
          resolve();
          return;
        }
        onStart?.();
      };
      currentAudio.onended = () => { URL.revokeObjectURL(url); currentAudio = null; onEnd?.(); resolve(); };
      currentAudio.onerror = () => { URL.revokeObjectURL(url); currentAudio = null; onEnd?.(); reject(new Error('Playback failed')); };
      currentAudio.play().catch((err) => { URL.revokeObjectURL(url); currentAudio = null; onEnd?.(); reject(err); });
    });

  } catch (err) {
    currentAbort = null;
    throw err; // re-throw so caller can handle AbortError / fallback
  }
}

// ── Web Speech API fallback ───────────────────────────────────────
function speakWebSpeech(text, lang, myGen, onStart, onEnd) {
  const synth = window.speechSynthesis;
  if (!synth) { onEnd?.(); return; }

  synth.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang   = lang === 'hi' ? 'hi-IN' : 'en-IN';
  utterance.rate   = 0.92;
  utterance.pitch  = 1.0;
  utterance.volume = 1.0;

  const voicePriority = lang === 'hi'
    ? [
        v => v.lang === 'hi-IN' && /natural|neural/i.test(v.name),
        v => v.lang === 'hi-IN' && /swara/i.test(v.name),
        v => v.lang === 'hi-IN' && /kalpana/i.test(v.name),
        v => v.lang === 'hi-IN' && /aditi/i.test(v.name),
        v => v.lang === 'hi-IN',
        v => /aditi/i.test(v.name),
        v => v.lang.startsWith('hi'),
        v => v.lang.includes('IN'),
      ]
    : [
        v => v.lang === 'en-IN' && /natural|neural/i.test(v.name),
        v => v.lang === 'en-IN' && /neerja/i.test(v.name),
        v => v.lang === 'en-IN' && /ravi/i.test(v.name),
        v => v.lang === 'en-IN' && /heera/i.test(v.name),
        v => v.lang === 'en-IN',
        v => /neerja|ravi|heera/i.test(v.name),
        v => /indian/i.test(v.name),
        v => v.lang.includes('IN'),
      ];

  function selectVoice() {
    const voices = synth.getVoices();
    for (const pred of voicePriority) {
      const found = voices.find(pred);
      if (found) { utterance.voice = found; break; }
    }
  }

  function speak() {
    // Generation guard — if stopSpeaking() was called before voiceschanged fired,
    // this callback is a ghost and must not start speaking.
    if (speechGen !== myGen) { onEnd?.(); return; }
    utterance.onstart = () => onStart?.();
    utterance.onend   = () => onEnd?.();
    utterance.onerror = () => onEnd?.();
    synth.speak(utterance);
  }

  if (synth.getVoices().length > 0) {
    selectVoice(); speak();
  } else {
    synth.addEventListener('voiceschanged', () => { selectVoice(); speak(); }, { once: true });
  }
}

// ── Public API ────────────────────────────────────────────────────

/**
 * Speak text using ElevenLabs voice, falling back to Web Speech API.
 */
export async function speakIndian(text, lang = 'en', onStart, onEnd) {
  // stopSpeaking() increments speechGen — capture AFTER so myGen stays in sync
  stopSpeaking();
  const myGen = speechGen;

  const clean = cleanText(text);
  if (!clean) { onEnd?.(); return; }

  const voiceId    = config.ELEVENLABS_VOICE_ID;
  const hasValidKey = voiceId && !voiceId.startsWith('your_');

  if (hasValidKey) {
    try {
      await speakElevenLabs(clean, voiceId, myGen, onStart, onEnd);
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;       // cancelled — stay silent
      if (speechGen !== myGen) return;             // superseded by newer call
      console.warn('[ElevenLabs TTS] Falling back to Web Speech API:', err.message);
    }
  }

  // Only fall back if this generation is still the active one
  if (speechGen === myGen) {
    speakWebSpeech(clean, lang, myGen, onStart, onEnd);
  }
}

/**
 * Stop all speech immediately — safe to call at any point in the pipeline.
 */
export function stopSpeaking() {
  // Invalidate any pending async callbacks
  speechGen++;

  // Cancel in-flight ElevenLabs fetch or arrayBuffer download
  if (currentAbort) {
    currentAbort.abort();
    currentAbort = null;
  }

  // Stop already-playing audio
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = '';
    currentAudio = null;
  }

  // Cancel Web Speech API
  window.speechSynthesis?.cancel();
}

/**
 * List available Indian voices (for debugging).
 */
export function getIndianVoices() {
  const voices = window.speechSynthesis?.getVoices() || [];
  return voices.filter(v => v.lang.includes('IN') || v.name.toLowerCase().includes('india'));
}
